import { Component, type ErrorInfo, type ReactNode } from "react";
import { generateErrorId } from "#shared/errors/errorId.ts";
import { getErrorDefinition } from "#shared/errors/registry.ts";
import { Button } from "@web/components/ui/button.tsx";
import { EmptyState } from "@web/components/EmptyState.tsx";
import { coyLog } from "@web/lib/coyLog.ts";
import { CopyDetailsButton } from "./CopyDetailsButton.tsx";

interface State {
  errorId: string;
  message: string;
}

/**
 * App-level boundary — render crashes become a friendly card, never a white page.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { errorId: "", message: "" };

  static getDerivedStateFromError(error: Error): State {
    return {
      errorId: generateErrorId(),
      message: error.message || getErrorDefinition("UNKNOWN").message,
    };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    coyLog.error(`Render crash ${this.state.errorId}`, {
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });
  }

  override render(): ReactNode {
    if (!this.state.errorId) return this.props.children;

    const def = getErrorDefinition("UNKNOWN");
    return (
      <div className="flex h-full min-h-dvh items-center justify-center bg-bg p-6">
        <EmptyState
          title={def.title}
          description={
            <>
              <span>{def.message}</span>
              <span className="mt-2 block font-mono text-xs text-faint">
                {def.code} · {this.state.errorId}
              </span>
            </>
          }
          action={
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button type="button" size="sm" onClick={() => window.location.reload()}>
                Reload
              </Button>
              <CopyDetailsButton
                errorId={this.state.errorId}
                code="UNKNOWN"
                message={this.state.message}
                action="render"
              />
            </div>
          }
        />
      </div>
    );
  }
}
